"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import { CreateDropWaveWrapper } from "@/components/waves/CreateDropWaveWrapper";
import { WaveDropsAllWithoutProvider } from "@/components/waves/drops/wave-drops-all";
import { WaveGallery } from "@/components/waves/gallery";
import { WaveLeaderboardCurationDropModal } from "@/components/waves/leaderboard/create/WaveLeaderboardCurationDropModal";
import { WaveDropCreate } from "@/components/waves/leaderboard/create/WaveDropCreate";
import MobileMemesArtSubmissionBtn from "@/components/waves/memes/submission/MobileMemesArtSubmissionBtn";
import PrivilegedDropCreator, { DropMode } from "@/components/waves/PrivilegedDropCreator";
import { useNotificationsContext } from "@/components/notifications/NotificationsContext";
import { UnreadDividerProvider, useUnreadDivider } from "@/contexts/wave/UnreadDividerContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiWave } from "@/generated/models/ApiWave";
import { getHomeRoute } from "@/helpers/navigation.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { resolveWaveSubmissionExperience, WaveSubmissionExperience } from "@/helpers/waves/wave-submission-experience.helpers";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { useMarkWaveNotificationsRead } from "@/hooks/useMarkWaveNotificationsRead";
import { useWave } from "@/hooks/useWave";
import { useApprovalWaveStatus } from "@/hooks/waves/useApprovalWaveStatus";
import type { WaveViewMode } from "@/hooks/useWaveViewMode";
import { selectEditingDropId } from "@/store/editSlice";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { ActiveDropAction } from "@/types/dropInteractionTypes";
import { ACCEPTED_FILE_TYPE_LABELS, isSupportedUploadFile } from "@/services/uploads/mediaUploadMimeType";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLayout } from "./layout/LayoutContext";
import { useWaveChatLeaveCleanup } from "./useWaveChatLeaveCleanup";
import { WaveChatSubmitDropModal } from "./WaveChatSubmitDropModal";
import type { ChatSubmitDropAction, ChatSubmitDropState } from "./chatSubmitDrop.types";
interface InitialDropState {
  readonly waveId: string;
  readonly serialNo: number;
  readonly dividerSerialNo: number | null;
}
interface MyStreamWaveChatProps {
  readonly wave: ApiWave;
  readonly firstUnreadSerialNo: number | null;
  readonly viewMode: WaveViewMode;
  readonly onDropClick: (drop: ExtendedDrop) => void;
  readonly chatSubmitDrop?: ChatSubmitDropState | null | undefined;
  readonly chatSubmitDropAction?: ChatSubmitDropAction | null | undefined;
  readonly onCloseChatSubmitDrop?: (() => void) | undefined;
}
interface WaveChatLeaveHandlerProps {
  readonly enabled: boolean;
  readonly waveId: string;
}
const MAX_UNSUPPORTED_FILE_NAMES_IN_TOAST = 3;
const noop = () => {};
const WaveChatLeaveHandler: React.FC<WaveChatLeaveHandlerProps> = t0 => {
  const $ = _c(6);
  const {
    enabled,
    waveId
  } = t0;
  const {
    setUnreadDividerSerialNo
  } = useUnreadDivider();
  const {
    removeWaveDeliveredNotifications
  } = useNotificationsContext();
  const markWaveNotificationsRead = useMarkWaveNotificationsRead();
  let t1;
  if ($[0] !== enabled || $[1] !== markWaveNotificationsRead || $[2] !== removeWaveDeliveredNotifications || $[3] !== setUnreadDividerSerialNo || $[4] !== waveId) {
    t1 = {
      enabled,
      waveId,
      setUnreadDividerSerialNo,
      removeWaveDeliveredNotifications,
      markWaveNotificationsRead
    };
    $[0] = enabled;
    $[1] = markWaveNotificationsRead;
    $[2] = removeWaveDeliveredNotifications;
    $[3] = setUnreadDividerSerialNo;
    $[4] = waveId;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  useWaveChatLeaveCleanup(t1);
  return null;
};
const MyStreamWaveChat: React.FC<MyStreamWaveChatProps> = t0 => {
  const $ = _c(110);
  const {
    wave,
    firstUnreadSerialNo,
    viewMode,
    onDropClick,
    chatSubmitDrop: t1,
    chatSubmitDropAction: t2,
    onCloseChatSubmitDrop
  } = t0;
  const chatSubmitDrop = t1 === undefined ? null : t1;
  const chatSubmitDropAction = t2 === undefined ? null : t2;
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const galleryContainerRef = useRef(null);
  const dragCounterRef = useRef(0);
  const externalAttachmentDropTokenRef = useRef(0);
  const {
    connectedProfile,
    setToast
  } = useAuth();
  const {
    isMemesWave,
    isCurationWave,
    isQuorumWave
  } = useWave(wave);
  const t3 = wave.participation.submission_strategy ?? null;
  let t4;
  if ($[0] !== isCurationWave || $[1] !== isMemesWave || $[2] !== isQuorumWave || $[3] !== t3) {
    t4 = resolveWaveSubmissionExperience({
      isMemesWave,
      isCurationWave,
      isQuorumWave,
      submissionStrategy: t3
    });
    $[0] = isCurationWave;
    $[1] = isMemesWave;
    $[2] = isQuorumWave;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const submissionExperience = t4;
  const editingDropId = useSelector(selectEditingDropId);
  const {
    isApp
  } = useDeviceInfo();
  const [isDragDropActive, setIsDragDropActive] = useState(false);
  const [externalAttachmentDrop, setExternalAttachmentDrop] = useState(null);
  let t5;
  if ($[5] !== wave.id) {
    t5 = {
      waveId: wave.id,
      activeDrop: null
    };
    $[5] = wave.id;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const [activeDropState, setActiveDropState] = useState(t5);
  const activeDrop = activeDropState.waveId === wave.id ? activeDropState.activeDrop : null;
  let t6;
  if ($[7] !== wave.id) {
    t6 = nextActiveDrop => {
      setActiveDropState({
        waveId: wave.id,
        activeDrop: nextActiveDrop
      });
    };
    $[7] = wave.id;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const setActiveDropForWave = t6;
  let t7;
  if ($[9] !== firstUnreadSerialNo || $[10] !== searchParams || $[11] !== wave.id) {
    bb0: {
      const dropParam = searchParams.get("serialNo");
      if (!dropParam) {
        t7 = null;
        break bb0;
      }
      const parsed = Number.parseInt(dropParam, 10);
      if (!Number.isFinite(parsed)) {
        t7 = null;
        break bb0;
      }
      let t8;
      if ($[13] !== firstUnreadSerialNo || $[14] !== searchParams) {
        const dividerParam = searchParams.get("divider");
        const dividerParsed = dividerParam ? Number.parseInt(dividerParam, 10) : null;
        t8 = dividerParsed !== null && Number.isFinite(dividerParsed) ? dividerParsed : firstUnreadSerialNo;
        $[13] = firstUnreadSerialNo;
        $[14] = searchParams;
        $[15] = t8;
      } else {
        t8 = $[15];
      }
      const dividerSerialNo = t8;
      let t9;
      if ($[16] !== dividerSerialNo || $[17] !== parsed || $[18] !== wave.id) {
        t9 = {
          waveId: wave.id,
          serialNo: parsed,
          dividerSerialNo
        };
        $[16] = dividerSerialNo;
        $[17] = parsed;
        $[18] = wave.id;
        $[19] = t9;
      } else {
        t9 = $[19];
      }
      t7 = t9;
    }
    $[9] = firstUnreadSerialNo;
    $[10] = searchParams;
    $[11] = wave.id;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const initialDropState = t7;
  let t8;
  if ($[20] !== firstUnreadSerialNo || $[21] !== initialDropState || $[22] !== wave.id) {
    t8 = () => {
      if (initialDropState?.waveId === wave.id && initialDropState.dividerSerialNo !== null) {
        return {
          waveId: wave.id,
          serialNo: initialDropState.dividerSerialNo
        };
      }
      return {
        waveId: wave.id,
        serialNo: firstUnreadSerialNo
      };
    };
    $[20] = firstUnreadSerialNo;
    $[21] = initialDropState;
    $[22] = wave.id;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const [capturedDividerState] = useState(t8);
  const scrollTarget = initialDropState?.waveId === wave.id ? initialDropState.serialNo : null;
  let dividerTarget = firstUnreadSerialNo;
  if (initialDropState?.waveId === wave.id) {
    dividerTarget = initialDropState.dividerSerialNo;
  } else {
    if (capturedDividerState.waveId === wave.id) {
      dividerTarget = capturedDividerState.serialNo;
    }
  }
  let t10;
  let t9;
  if ($[24] !== initialDropState || $[25] !== pathname || $[26] !== router || $[27] !== searchParams || $[28] !== viewMode) {
    t9 = () => {
      if (!initialDropState || viewMode === "gallery") {
        return;
      }
      const params = new URLSearchParams(searchParams.toString() || "");
      if (!params.has("serialNo") && !params.has("divider")) {
        return;
      }
      params.delete("serialNo");
      params.delete("divider");
      const href = params.toString() ? `${pathname}?${params.toString()}` : pathname || getHomeRoute();
      router.replace(href, {
        scroll: false
      });
    };
    t10 = [initialDropState, pathname, router, searchParams, viewMode];
    $[24] = initialDropState;
    $[25] = pathname;
    $[26] = router;
    $[27] = searchParams;
    $[28] = viewMode;
    $[29] = t10;
    $[30] = t9;
  } else {
    t10 = $[29];
    t9 = $[30];
  }
  useEffect(t9, t10);
  const {
    waveViewStyle
  } = useLayout();
  let t11;
  if ($[31] !== setActiveDropForWave) {
    const onReply = (drop, partId) => {
      setActiveDropForWave({
        action: ActiveDropAction.REPLY,
        drop,
        partId
      });
    };
    t11 = t12 => {
      const {
        drop: drop_0,
        partId: partId_0
      } = t12;
      onReply(drop_0, partId_0);
    };
    $[31] = setActiveDropForWave;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  const handleReply = t11;
  let t12;
  if ($[33] !== setActiveDropForWave) {
    t12 = () => {
      setActiveDropForWave(null);
    };
    $[33] = setActiveDropForWave;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  const onCancelReplyQuote = t12;
  let t13;
  if ($[35] !== wave) {
    t13 = {
      wave
    };
    $[35] = wave;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  const {
    winningThreshold,
    isVotingClosed,
    isVotingControlsLocked
  } = useApprovalWaveStatus(t13);
  const fixedDropMode = DropMode.CHAT;
  const activeChatSubmitDropExperience = chatSubmitDrop?.submissionExperience ?? null;
  const showFixedParticipationSubmit = activeChatSubmitDropExperience !== null && activeChatSubmitDropExperience !== WaveSubmissionExperience.CURATION_LEGACY && activeChatSubmitDropExperience !== WaveSubmissionExperience.MEMES_LEGACY && activeChatSubmitDropExperience !== WaveSubmissionExperience.QUORUM_PROPOSAL;
  const showQuorumProposalSubmit = activeChatSubmitDropExperience === WaveSubmissionExperience.QUORUM_PROPOSAL;
  const showCurationSubmitModal = activeChatSubmitDropExperience === WaveSubmissionExperience.CURATION_LEGACY;
  const closeChatSubmitDrop = onCloseChatSubmitDrop ?? noop;
  const chatCurationUrlSubmitHandler = chatSubmitDropAction?.isVisible === true ? chatSubmitDropAction.onOpenWithCurationUrl : undefined;
  const canSubmitChatCurationUrl = chatSubmitDropAction?.isVisible === true && chatSubmitDropAction.canOpen;
  const chatCurationUrlSubmitRestrictionMessage = chatSubmitDropAction?.isVisible === true ? chatSubmitDropAction.restrictionMessage : null;
  const isChatSubmitFlowOpen = activeChatSubmitDropExperience !== null;
  const shouldHandleContainerFileDrop = _temp;
  const isFileDragEvent = _temp2;
  const filterSupportedFiles = _temp3;
  let t14;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      dragCounterRef.current = 0;
      setIsDragDropActive(false);
    };
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  const resetDragDropState = t14;
  let t15;
  let t16;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      globalThis.window.addEventListener("dragend", resetDragDropState);
      globalThis.window.addEventListener("drop", resetDragDropState);
      return () => {
        globalThis.window.removeEventListener("dragend", resetDragDropState);
        globalThis.window.removeEventListener("drop", resetDragDropState);
      };
    };
    t16 = [resetDragDropState];
    $[38] = t15;
    $[39] = t16;
  } else {
    t15 = $[38];
    t16 = $[39];
  }
  useEffect(t15, t16);
  let t17;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = event_1 => {
      if (!isFileDragEvent(event_1) || !shouldHandleContainerFileDrop(event_1)) {
        return;
      }
      event_1.preventDefault();
      event_1.stopPropagation();
      dragCounterRef.current = dragCounterRef.current + 1;
      setIsDragDropActive(true);
    };
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  const onContainerDragEnter = t17;
  let t18;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = event_2 => {
      if (!isFileDragEvent(event_2) || !shouldHandleContainerFileDrop(event_2)) {
        return;
      }
      event_2.preventDefault();
      event_2.stopPropagation();
      event_2.dataTransfer.dropEffect = "copy";
    };
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  const onContainerDragOver = t18;
  let t19;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = event_3 => {
      if (!isFileDragEvent(event_3) || !shouldHandleContainerFileDrop(event_3)) {
        return;
      }
      event_3.preventDefault();
      event_3.stopPropagation();
      dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
      if (dragCounterRef.current === 0) {
        setIsDragDropActive(false);
      }
    };
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  const onContainerDragLeave = t19;
  let onContainerDrop;
  if ($[43] !== setToast) {
    onContainerDrop = event_4 => {
      if (!isFileDragEvent(event_4) || !shouldHandleContainerFileDrop(event_4)) {
        return;
      }
      event_4.preventDefault();
      event_4.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragDropActive(false);
      const droppedFiles = Array.from(event_4.dataTransfer.files);
      if (droppedFiles.length === 0) {
        return;
      }
      handleExternalAttachmentFiles(droppedFiles);
    };
    const formatUnsupportedFileNames = _temp5;
    const handleExternalAttachmentFiles = files_0 => {
      if (files_0.length === 0) {
        return;
      }
      const {
        supported: supported_0,
        unsupported: unsupported_1
      } = filterSupportedFiles(files_0);
      if (supported_0.length > 0) {
        externalAttachmentDropTokenRef.current = externalAttachmentDropTokenRef.current + 1;
        setExternalAttachmentDrop({
          token: externalAttachmentDropTokenRef.current,
          files: supported_0
        });
      }
      if (unsupported_1.length > 0) {
        const unsupportedNames = formatUnsupportedFileNames(unsupported_1);
        setToast({
          message: `Unsupported file type: ${unsupportedNames}. Accepted Types: ${ACCEPTED_FILE_TYPE_LABELS}`,
          type: "error"
        });
      }
    };
    $[43] = setToast;
    $[44] = onContainerDrop;
  } else {
    onContainerDrop = $[44];
  }
  if (viewMode === "gallery") {
    let t20;
    if ($[45] !== onDropClick || $[46] !== wave) {
      t20 = <WaveGallery wave={wave} onDropClick={onDropClick} />;
      $[45] = onDropClick;
      $[46] = wave;
      $[47] = t20;
    } else {
      t20 = $[47];
    }
    let t21;
    if ($[48] !== t20 || $[49] !== waveViewStyle) {
      t21 = <div ref={galleryContainerRef} className="tw-flex tw-h-full tw-w-full tw-flex-col tw-overflow-hidden" style={waveViewStyle}>{t20}</div>;
      $[48] = t20;
      $[49] = waveViewStyle;
      $[50] = t21;
    } else {
      t21 = $[50];
    }
    return t21;
  }
  const t20 = dividerTarget ?? null;
  const t21 = `unread-divider-${wave.id}`;
  const t22 = Boolean(connectedProfile?.handle);
  let t23;
  if ($[51] !== t22 || $[52] !== wave.id) {
    t23 = <WaveChatLeaveHandler enabled={t22} waveId={wave.id} />;
    $[51] = t22;
    $[52] = wave.id;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== isDragDropActive) {
    t24 = isDragDropActive && <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-z-40 tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border-2 tw-border-dotted tw-border-primary-400 tw-bg-iron-900/75 tw-p-6"><div className="tw-max-w-3xl tw-p-4 tw-text-center"><p className="tw-text-base tw-font-semibold tw-text-primary-300">Drop files here</p><p className="tw-mt-2 tw-text-xs tw-text-iron-300">Accepted types: {ACCEPTED_FILE_TYPE_LABELS}</p></div></div>;
    $[54] = isDragDropActive;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== activeDrop || $[57] !== dividerTarget || $[58] !== handleReply || $[59] !== isVotingClosed || $[60] !== isVotingControlsLocked || $[61] !== onDropClick || $[62] !== scrollTarget || $[63] !== wave || $[64] !== winningThreshold) {
    t25 = <WaveDropsAllWithoutProvider key={wave.id} waveId={wave.id} wave={wave} onReply={handleReply} activeDrop={activeDrop} initialDrop={scrollTarget} dividerSerialNo={dividerTarget} unreadCount={wave.metrics.your_unread_drops_count} dropId={null} onDropContentClick={onDropClick} isMuted={wave.metrics.muted} winningThreshold={winningThreshold} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} />;
    $[56] = activeDrop;
    $[57] = dividerTarget;
    $[58] = handleReply;
    $[59] = isVotingClosed;
    $[60] = isVotingControlsLocked;
    $[61] = onDropClick;
    $[62] = scrollTarget;
    $[63] = wave;
    $[64] = winningThreshold;
    $[65] = t25;
  } else {
    t25 = $[65];
  }
  let t26;
  if ($[66] !== activeDrop || $[67] !== canSubmitChatCurationUrl || $[68] !== chatCurationUrlSubmitHandler || $[69] !== chatCurationUrlSubmitRestrictionMessage || $[70] !== editingDropId || $[71] !== externalAttachmentDrop || $[72] !== isApp || $[73] !== isChatSubmitFlowOpen || $[74] !== onCancelReplyQuote || $[75] !== wave) {
    t26 = !(isApp && editingDropId) && <div className="tw-mt-auto"><CreateDropWaveWrapper><PrivilegedDropCreator activeDrop={activeDrop} onCancelReplyQuote={onCancelReplyQuote} onDropAddedToQueue={onCancelReplyQuote} wave={wave} dropId={null} fixedDropMode={fixedDropMode} externalAttachmentDrop={externalAttachmentDrop} onExternalAttachmentDropConsumed={() => setExternalAttachmentDrop(null)} onSubmitCurationUrl={chatCurationUrlSubmitHandler} canSubmitCurationUrl={canSubmitChatCurationUrl} curationUrlSubmitRestrictionMessage={chatCurationUrlSubmitRestrictionMessage} termsSignatureFlowEnabled={!isChatSubmitFlowOpen} /></CreateDropWaveWrapper></div>;
    $[66] = activeDrop;
    $[67] = canSubmitChatCurationUrl;
    $[68] = chatCurationUrlSubmitHandler;
    $[69] = chatCurationUrlSubmitRestrictionMessage;
    $[70] = editingDropId;
    $[71] = externalAttachmentDrop;
    $[72] = isApp;
    $[73] = isChatSubmitFlowOpen;
    $[74] = onCancelReplyQuote;
    $[75] = wave;
    $[76] = t26;
  } else {
    t26 = $[76];
  }
  let t27;
  if ($[77] !== chatSubmitDrop?.initialCurationUrl || $[78] !== closeChatSubmitDrop || $[79] !== showFixedParticipationSubmit || $[80] !== wave) {
    t27 = showFixedParticipationSubmit && <WaveChatSubmitDropModal isOpen={true} wave={wave} title="Submit drop" initialCurationUrl={chatSubmitDrop?.initialCurationUrl ?? null} onClose={closeChatSubmitDrop} />;
    $[77] = chatSubmitDrop?.initialCurationUrl;
    $[78] = closeChatSubmitDrop;
    $[79] = showFixedParticipationSubmit;
    $[80] = wave;
    $[81] = t27;
  } else {
    t27 = $[81];
  }
  let t28;
  if ($[82] !== closeChatSubmitDrop || $[83] !== showQuorumProposalSubmit || $[84] !== wave) {
    t28 = showQuorumProposalSubmit && <WaveDropCreate wave={wave} onCancel={closeChatSubmitDrop} onSuccess={closeChatSubmitDrop} onExitFixedDropMode={closeChatSubmitDrop} isModalContent={true} />;
    $[82] = closeChatSubmitDrop;
    $[83] = showQuorumProposalSubmit;
    $[84] = wave;
    $[85] = t28;
  } else {
    t28 = $[85];
  }
  let t29;
  if ($[86] !== chatSubmitDrop?.initialCurationUrl || $[87] !== closeChatSubmitDrop || $[88] !== showCurationSubmitModal || $[89] !== wave) {
    t29 = showCurationSubmitModal && <WaveLeaderboardCurationDropModal isOpen={true} wave={wave} initialUrl={chatSubmitDrop?.initialCurationUrl ?? null} onClose={closeChatSubmitDrop} />;
    $[86] = chatSubmitDrop?.initialCurationUrl;
    $[87] = closeChatSubmitDrop;
    $[88] = showCurationSubmitModal;
    $[89] = wave;
    $[90] = t29;
  } else {
    t29 = $[90];
  }
  let t30;
  if ($[91] !== isVotingControlsLocked || $[92] !== submissionExperience || $[93] !== wave) {
    t30 = submissionExperience === WaveSubmissionExperience.MEMES_LEGACY && <MobileMemesArtSubmissionBtn wave={wave} isSubmissionLocked={isVotingControlsLocked} />;
    $[91] = isVotingControlsLocked;
    $[92] = submissionExperience;
    $[93] = wave;
    $[94] = t30;
  } else {
    t30 = $[94];
  }
  let t31;
  if ($[95] !== onContainerDrop || $[96] !== t24 || $[97] !== t25 || $[98] !== t26 || $[99] !== t27 || $[100] !== t28 || $[101] !== t29 || $[102] !== t30 || $[103] !== waveViewStyle) {
    t31 = <section className="tw-w-full tw-flex tw-flex-col tw-overflow-y-auto tw-overflow-x-hidden lg:tw-scrollbar-thin tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 desktop-hover:hover:tw-scrollbar-thumb-iron-300 scroll-shadow tw-flex-grow tw-relative" style={waveViewStyle} aria-label="Wave chat file upload area" onDragEnter={onContainerDragEnter} onDragOver={onContainerDragOver} onDragLeave={onContainerDragLeave} onDrop={onContainerDrop}>{t24}{t25}{t26}{t27}{t28}{t29}{t30}</section>;
    $[95] = onContainerDrop;
    $[96] = t24;
    $[97] = t25;
    $[98] = t26;
    $[99] = t27;
    $[100] = t28;
    $[101] = t29;
    $[102] = t30;
    $[103] = waveViewStyle;
    $[104] = t31;
  } else {
    t31 = $[104];
  }
  let t32;
  if ($[105] !== t20 || $[106] !== t21 || $[107] !== t23 || $[108] !== t31) {
    t32 = <UnreadDividerProvider initialSerialNo={t20} key={t21}>{t23}{t31}</UnreadDividerProvider>;
    $[105] = t20;
    $[106] = t21;
    $[107] = t23;
    $[108] = t31;
    $[109] = t32;
  } else {
    t32 = $[109];
  }
  return t32;
};
export default MyStreamWaveChat;
function _temp(event) {
  const target = event.target;
  if (!(target instanceof Element)) {
    return true;
  }
  return target.closest("[contenteditable=\"true\"], input, textarea, [role=\"textbox\"]") === null;
}
function _temp2(event_0) {
  return Array.from(event_0.dataTransfer.types).includes("Files");
}
function _temp3(files) {
  const supported = [];
  const unsupported = [];
  files.forEach(file => {
    if (isSupportedUploadFile(file)) {
      supported.push(file);
    } else {
      unsupported.push(file);
    }
  });
  return {
    supported,
    unsupported
  };
}
function _temp4(file_0) {
  return file_0.name;
}
function _temp5(unsupported_0) {
  const displayedNames = unsupported_0.slice(0, MAX_UNSUPPORTED_FILE_NAMES_IN_TOAST).map(_temp4);
  const remainingCount = Math.max(0, unsupported_0.length - displayedNames.length);
  return remainingCount > 0 ? `${displayedNames.join(", ")} and ${remainingCount} more` : displayedNames.join(", ");
}